import React, { memo } from 'react';
import { Button, TextField, InputAdornment, Alert } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useDoctorShiftRuleList } from '../hook/useDoctorShiftRuleList';
import type { DoctorShiftRuleRepository } from '../../../../application/doctorShiftRule';
import DoctorShiftRuleTable from '../components/DoctorShiftRuleTable';
import DoctorShiftRuleAddModal from '../components/DoctorShiftRuleAddModal';
import DoctorShiftRuleEditModal from '../components/DoctorShiftRuleEditModal';
import DoctorShiftRuleDeleteDialog from '../components/DoctorShiftRuleDeleteDialog';
import '../../../../pages/ManagementList.css';

interface DoctorShiftRuleListContainerProps {
  repository: DoctorShiftRuleRepository;
}

/**
 * 医師シフトルール一覧コンテナ
 *
 * - useDoctorShiftRuleList Hook使用
 * - コンポーネント組み立て
 * - Repository注入
 */
const DoctorShiftRuleListContainer: React.FC<DoctorShiftRuleListContainerProps> = memo(({
  repository
}) => {
  const {
    data,
    meta,
    isLoading,
    error,
    addModalOpen,
    editModalOpen,
    deleteDialogOpen,
    selectedRule,
    handlePageChange,
    handleSearch,
    refresh,
    showAddModal,
    showEditModal,
    showDeleteDialog,
    closeModals,
    createRule,
    updateRule,
    deleteRule,
    clearError
  } = useDoctorShiftRuleList(repository);

  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearchSubmit = () => {
    handleSearch(searchTerm);
  };

  return (
    <div className="management-page">
      <div className="management-heading">
        <div><h1>医師シフトルール</h1><p>医師ごとのシフトルール（割り当て可能/禁止時間帯）を管理します</p></div>
        <div className="management-heading-actions">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            disabled={isLoading}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={showAddModal}
          >
            ルールを追加
          </Button>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            severity="error"
            onClose={clearError}
          >
            {error}
          </Alert>
        </div>
      )}

      {/* 検索フィルター */}
      <section className="management-panel" aria-label="シフトルールの検索">
        <form className="management-controls" role="search" onSubmit={event => { event.preventDefault(); handleSearchSubmit(); }}>
        <TextField
          size="small"
          placeholder="医師名または医師コードで検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 220, flex: '1 1 280px', maxWidth: 460 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
        >
          検索
        </Button>
        </form>
      </section>

      {/* テーブル */}
      <section className="management-panel" aria-label="シフトルール一覧">
        <div className="management-results-head"><h2>検索結果</h2><span>{meta?.total ?? data.length} 件の医師</span></div>
        <DoctorShiftRuleTable
          data={data}
          isLoading={isLoading}
          meta={meta}
          onPageChange={handlePageChange}
          onEdit={showEditModal}
          onDelete={showDeleteDialog}
        />
      </section>

      {/* 追加モーダル */}
      <DoctorShiftRuleAddModal
        open={addModalOpen}
        onClose={closeModals}
        onSubmit={createRule}
        isLoading={isLoading}
        error={error}
      />

      {/* 編集モーダル */}
      <DoctorShiftRuleEditModal
        open={editModalOpen}
        rule={selectedRule}
        onClose={closeModals}
        onSubmit={updateRule}
        isLoading={isLoading}
        error={error}
      />

      {/* 削除確認ダイアログ */}
      <DoctorShiftRuleDeleteDialog
        open={deleteDialogOpen}
        rule={selectedRule}
        onClose={closeModals}
        onConfirm={deleteRule}
        isLoading={isLoading}
      />
    </div>
  );
});

DoctorShiftRuleListContainer.displayName = 'DoctorShiftRuleListContainer';

export default DoctorShiftRuleListContainer;
